import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import type { LockRequest } from '../../services/financialYearsApi';

interface LockDialogProps {
  open: boolean;
  fyLabel: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: LockRequest) => void;
  submitting: boolean;
}

/**
 * Post-audit terminal action. The Confirm button stays disabled until the operator
 * types the FY label exactly and supplies audit firm + report reference. Once locked,
 * there is no path out — the dialog copy makes that explicit.
 */
export const LockDialog = ({
  open,
  fyLabel,
  onOpenChange,
  onSubmit,
  submitting,
}: LockDialogProps) => {
  const [typedLabel, setTypedLabel] = useState('');
  const [auditFirm, setAuditFirm] = useState('');
  const [auditRef, setAuditRef] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTypedLabel('');
      setAuditFirm('');
      setAuditRef('');
      setEvidenceUrl('');
    }
    onOpenChange(next);
  };

  const labelMatches = typedLabel.trim() === fyLabel;
  const canSubmit =
    labelMatches &&
    auditFirm.trim().length > 0 &&
    auditRef.trim().length > 0 &&
    !submitting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-700">
            <Lock className="size-5" /> Lock {fyLabel} — terminal
          </DialogTitle>
          <DialogDescription>
            Locking marks <strong>{fyLabel}</strong> as audited and signed off. There is no path out of
            this state — no permission unlocks, no API does. Adjustments to a locked year must be posted
            forward in the current open year.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="audit-firm">Audit firm name</Label>
            <Input
              id="audit-firm"
              value={auditFirm}
              onChange={(e) => setAuditFirm(e.target.value)}
              placeholder="e.g. PKF Eastern Africa"
              disabled={submitting}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="audit-ref">Audit report reference</Label>
            <Input
              id="audit-ref"
              value={auditRef}
              onChange={(e) => setAuditRef(e.target.value)}
              placeholder="e.g. PKF-2026-001"
              disabled={submitting}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="evidence-url">Audit sign-off evidence URL (optional)</Label>
            <Input
              id="evidence-url"
              type="url"
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
              placeholder="https://files.example.com/audit.pdf"
              disabled={submitting}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="lock-confirmation-label">
              Type <span className="font-mono font-semibold">{fyLabel}</span> to confirm
            </Label>
            <Input
              id="lock-confirmation-label"
              value={typedLabel}
              onChange={(e) => setTypedLabel(e.target.value)}
              placeholder={fyLabel}
              autoComplete="off"
              disabled={submitting}
              className="mt-1 font-mono"
            />
            {typedLabel.length > 0 && !labelMatches && (
              <p className="text-xs text-red-600 mt-1">Doesn't match. Type the label exactly as shown.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                confirmationLabel: typedLabel.trim(),
                auditFirmName: auditFirm.trim(),
                auditReportReference: auditRef.trim(),
                auditSignOffEvidenceUrl: evidenceUrl.trim() || null,
              })
            }
            disabled={!canSubmit}
            className="bg-purple-700 hover:bg-purple-800"
          >
            {submitting ? 'Locking…' : 'Lock'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
